<script setup lang="ts">
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { computed, h, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { Company, Property, Room, RoomAsset } from '@/models'
import { fetchCompanies } from '@/services/company'
import { deleteProperty, fetchPropertyById, updateProperty } from '@/services/properties'
import { deleteRoomAsset, fetchRoomAssets } from '@/services/roomAssets'
import { deleteRoom, fetchRooms, reorderRooms } from '@/services/rooms'
import { getDisplayUrl } from '@/services/storage'
import { useAuthStore } from '@/stores/auth'
import OtaSyncSettings from './OtaSyncSettings.vue'
import PropertyFormDialog from './PropertyFormDialog.vue'
import RoomAssetFormDialog from './RoomAssetFormDialog.vue'
import RoomFormDialog from './RoomFormDialog.vue'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()

const loading = ref(true)
const dialog = ref(false)
const property = ref<Property | null>(null)
const companies = ref<Company[]>([])
const activeTab = ref('overview')

const rooms = ref<Room[]>([])
const roomsLoading = ref(false)
const roomDialog = ref(false)
const editingRoom = ref<Room | null>(null)

const roomAssets = ref<Map<string, RoomAsset[]>>(new Map())
const expandedRooms = ref<Set<string>>(new Set())
const assetDialog = ref(false)
const editingAsset = ref<RoomAsset | null>(null)
const assetRoomId = ref('')

const companyName = computed(() => {
  if (!property.value) return ''
  const company = companies.value.find((item) => item.id === property.value?.company_id)
  return company?.name || property.value.company_id
})

const propertyImage = ref('https://placehold.co/800x550?text=Property')

watch(
  property,
  async (val) => {
    propertyImage.value = await getDisplayUrl(val?.image)
  },
  { immediate: true },
)

const canEdit = computed(() => {
  if (!property.value) return false
  return auth.can('properties', 'edit', property.value.company_id)
})

const canDelete = computed(() => {
  if (!property.value) return false
  return auth.can('properties', 'delete', property.value.company_id)
})

const roomTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    bedroom: 'Bedroom',
    bathroom: 'Bathroom',
    kitchen: 'Kitchen',
    living_room: 'Living Room',
    dining: 'Dining',
    hallway: 'Hallway',
    balcony: 'Balcony',
    other: 'Other',
  }
  return labels[type] || type
}

const loadProperty = async () => {
  loading.value = true
  try {
    const [propertyRes, companiesRes] = await Promise.all([
      fetchPropertyById(String(route.params.id || '')),
      fetchCompanies(),
    ])

    if (!propertyRes) {
      message.error('Property not found.')
      router.push('/properties')
      return
    }

    property.value = propertyRes
    companies.value = companiesRes
  } catch (error: any) {
    message.error(error?.message || 'Failed to load property details.')
    router.push('/properties')
  } finally {
    loading.value = false
  }
}

const loadRooms = async () => {
  if (!property.value) return
  roomsLoading.value = true
  try {
    rooms.value = await fetchRooms(property.value.id)
  } catch (e: any) {
    message.error(e?.message || 'Failed to load rooms')
  } finally {
    roomsLoading.value = false
  }
}

const loadRoomAssets = async (roomId: string) => {
  try {
    const assets = await fetchRoomAssets(roomId)
    roomAssets.value.set(roomId, assets)
  } catch {
    roomAssets.value.set(roomId, [])
  }
}

const handleSaveProperty = async (payload: Partial<Property>) => {
  if (!property.value) return
  try {
    const updated = await updateProperty(property.value.id, payload)
    property.value = updated
    dialog.value = false
    message.success('Property updated successfully!')
  } catch (error: any) {
    message.error(error?.message || 'Failed to update property.')
  }
}

const handleDelete = async () => {
  if (!property.value) return
  try {
    await deleteProperty(property.value.id)
    message.success('Property deleted successfully!')
    router.push('/properties')
  } catch (error: any) {
    message.error(error?.message || 'Failed to delete property.')
  }
}

const openNewRoom = () => {
  editingRoom.value = null
  roomDialog.value = true
}

const openEditRoom = (room: Room) => {
  editingRoom.value = room
  roomDialog.value = true
}

const handleDeleteRoom = async (room: Room) => {
  try {
    await deleteRoom(room.id)
    message.success('Room deleted')
    await loadRooms()
  } catch (e: any) {
    message.error(e?.message || 'Failed to delete room')
  }
}

const handleRoomSaved = () => {
  loadRooms()
}

const toggleRoomExpand = (roomId: string) => {
  if (expandedRooms.value.has(roomId)) {
    expandedRooms.value.delete(roomId)
  } else {
    expandedRooms.value.add(roomId)
    if (!roomAssets.value.has(roomId)) {
      loadRoomAssets(roomId)
    }
  }
}

const moveRoomUp = async (index: number) => {
  if (index <= 0) return
  const ids = rooms.value.map((r) => r.id)
  const temp = ids[index - 1]!
  ids[index - 1] = ids[index]!
  ids[index] = temp
  await reorderRooms(ids)
  await loadRooms()
}

const moveRoomDown = async (index: number) => {
  if (index >= rooms.value.length - 1) return
  const ids = rooms.value.map((r) => r.id)
  const temp = ids[index + 1]!
  ids[index + 1] = ids[index]!
  ids[index] = temp
  await reorderRooms(ids)
  await loadRooms()
}

const openNewAsset = (roomId: string) => {
  editingAsset.value = null
  assetRoomId.value = roomId
  assetDialog.value = true
}

const openEditAsset = (asset: RoomAsset) => {
  editingAsset.value = asset
  assetRoomId.value = asset.room_id
  assetDialog.value = true
}

const handleDeleteAsset = async (asset: RoomAsset) => {
  try {
    await deleteRoomAsset(asset.id)
    message.success('Asset removed')
    await loadRoomAssets(asset.room_id)
  } catch (e: any) {
    message.error(e?.message || 'Failed to delete asset')
  }
}

const handleAssetSaved = () => {
  if (assetRoomId.value) {
    loadRoomAssets(assetRoomId.value)
  }
}

const roomAssetColumns = [
  { title: 'Item', dataIndex: 'item_name', key: 'item_name' },
  { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 80 },
  { title: 'Notes', dataIndex: 'notes', key: 'notes' },
  { title: 'Actions', key: 'actions', width: 120 },
]

onMounted(() => {
  loadProperty()
})
</script>

<template>
  <a-page-header title="Property Details" @back="router.go(-1)" />

  <a-spin :spinning="loading">
    <a-card v-if="property">
      <template #title>{{ property.title }}</template>
      <template #extra>
        <a-space>
          <a-button @click="dialog = true" :disabled="!canEdit">Edit</a-button>
          <a-popconfirm
            :title="`Delete ${property.title}`"
            description="Are you sure you want to delete this property?"
            ok-text="Yes"
            cancel-text="No"
            @confirm="handleDelete"
            :disabled="!canDelete"
          >
            <a-button danger :disabled="!canDelete">Delete</a-button>
          </a-popconfirm>
        </a-space>
      </template>

      <a-tabs v-model:value="activeTab" animated>
        <a-tab-pane key="overview" tab="Overview">
          <a-row :gutter="24">
            <a-col :xs="24" :md="10">
              <a-image
                :src="propertyImage"
                style="width: 100%; border-radius: 8px"
              />
            </a-col>
            <a-col :xs="24" :md="14">
              <a-descriptions :column="1" bordered>
                <a-descriptions-item label="Company">{{ companyName }}</a-descriptions-item>
                <a-descriptions-item label="Location">{{ property.location }}</a-descriptions-item>
                <a-descriptions-item label="Type">{{ property.type }}</a-descriptions-item>
                <a-descriptions-item label="Status">{{
                  property.status || 'active'
                }}</a-descriptions-item>
                <a-descriptions-item label="Price"
                  >₱{{ Number(property.price || 0).toLocaleString('en-PH') }}</a-descriptions-item
                >
                <a-descriptions-item label="Beds">{{ property.beds }}</a-descriptions-item>
                <a-descriptions-item label="Baths">{{ property.baths }}</a-descriptions-item>
                <a-descriptions-item label="Area">{{ property.sqm }} m²</a-descriptions-item>
                <a-descriptions-item label="Description">{{
                  property.description || 'No description provided.'
                }}</a-descriptions-item>
                <a-descriptions-item label="Amenities">
                  <a-flex v-if="property.amenities?.length" wrap="wrap" gap="4">
                    <a-tag v-for="a in property.amenities" :key="a">{{ a }}</a-tag>
                  </a-flex>
                  <span v-else style="color: var(--text-secondary)">None listed</span>
                </a-descriptions-item>
              </a-descriptions>
            </a-col>
          </a-row>
        </a-tab-pane>
        <a-tab-pane key="rooms" tab="Rooms">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px">
            <h3 style="margin: 0">Rooms</h3>
            <a-button v-if="canEdit" type="primary" @click="openNewRoom"><plus-outlined /> Add Room</a-button>
          </div>

          <a-spin :spinning="roomsLoading">
            <div v-if="rooms.length === 0" style="text-align: center; padding: 32px">
              <a-empty description="No rooms yet. Add your first room." />
            </div>

            <div v-for="(room, index) in rooms" :key="room.id" style="margin-bottom: 12px">
              <a-card
                :class="{ 'room-card': true, expanded: expandedRooms.has(room.id) }"
                @click="toggleRoomExpand(room.id)"
                hoverable
                style="cursor: pointer"
              >
                <div style="display: flex; justify-content: space-between; align-items: center">
                  <div style="display: flex; align-items: center; gap: 12px">
                    <div>
                      <strong>{{ room.name }}</strong>
                      <a-tag style="margin-left: 8px">{{ roomTypeLabel(room.type) }}</a-tag>
                      <span style="color: var(--text-muted); font-size: 13px; margin-left: 8px">
                        Floor {{ room.floor }}
                      </span>
                    </div>
                  </div>
                  <div v-if="canEdit" @click.stop>
                    <a-space>
                      <a-button size="small" @click="moveRoomUp(index)" :disabled="index === 0">
                        <up-outlined />
                      </a-button>
                      <a-button size="small" @click="moveRoomDown(index)" :disabled="index === rooms.length - 1">
                        <down-outlined />
                      </a-button>
                      <a-button size="small" @click="openEditRoom(room)"><edit-outlined /></a-button>
                      <a-popconfirm title="Delete this room?" ok-text="Yes" cancel-text="No" @confirm="handleDeleteRoom(room)">
                        <a-button size="small" danger><delete-outlined /></a-button>
                      </a-popconfirm>
                    </a-space>
                  </div>
                </div>

                <div v-if="room.notes" style="margin-top: 8px; color: var(--text-muted); font-size: 13px">
                  {{ room.notes }}
                </div>
              </a-card>

              <div v-if="expandedRooms.has(room.id)" style="margin-left: 24px; margin-top: 8px">
                <a-card type="inner">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px">
                    <strong>Asset Checklist</strong>
                    <a-button v-if="canEdit" size="small" type="primary" @click="openNewAsset(room.id)"><plus-outlined /> Add Item</a-button>
                  </div>
                  <a-table
                    :data-source="roomAssets.get(room.id) || []"
                    :columns="roomAssetColumns"
                    :pagination="false"
                    size="small"
                    row-key="id"
                  >
                    <template #bodyCell="{ column, record }">
                      <template v-if="column.key === 'notes'">
                        {{ (record as RoomAsset).notes || '—' }}
                      </template>
                      <template v-if="column.key === 'actions'">
                        <a-space v-if="canEdit">
                          <a-button size="small" @click="openEditAsset(record as RoomAsset)"><edit-outlined /></a-button>
                          <a-popconfirm title="Remove this item?" ok-text="Yes" cancel-text="No" @confirm="handleDeleteAsset(record as RoomAsset)">
                            <a-button size="small" danger><delete-outlined /></a-button>
                          </a-popconfirm>
                        </a-space>
                      </template>
                    </template>
                  </a-table>
                </a-card>
              </div>
            </div>
          </a-spin>
        </a-tab-pane>
        <a-tab-pane key="ical" tab="OTA Calendar">
          <OtaSyncSettings :property-id="String(route.params.id || '')" />
        </a-tab-pane>
      </a-tabs>
    </a-card>
  </a-spin>

  <PropertyFormDialog
    v-model="dialog"
    :property="property"
    :companies="companies"
    @save="handleSaveProperty"
  />

  <RoomFormDialog
    v-model:visible="roomDialog"
    :room="editingRoom"
    :property-id="property?.id || ''"
    @saved="handleRoomSaved"
  />

  <RoomAssetFormDialog
    v-model:visible="assetDialog"
    :asset="editingAsset"
    :room-id="assetRoomId"
    @saved="handleAssetSaved"
  />
</template>

<style scoped>
.room-card {
  transition: border-color 0.2s;
}
.room-card.expanded {
  border-color: #1890ff;
}
</style>
